import {
  extractTemplateSlides,
  getValidationConfig,
  Template,
} from "@reside/forms";
import {pick} from "lodash";

import {getWorkflowPacketLabel} from "../../../../atoms/start-workflow/workflowMapping";
import {INCLUDE_IN_AUDIT_LOG_BLOCK} from "./consts";
import {
  filterVisibleFacilitySettingsBlocks,
  getFacilitySettingsBlockLabel,
} from "./facilitySettingsBlocks";
import {
  makeWorkflowBlockSectionId,
  makeWorkflowDocumentCategorySectionId,
  makeWorkflowDownloadEmptyPacketSectionId,
} from "./workflowSectionIds";

export type WorkflowNavItem = Readonly<{
  id: string;
  label: string;
}>;

/**
 * Parent (admission) facility settings fields that workflow-settings templates
 * are allowed to read, e.g. in visibility rules. Everything else must come
 * from the workflow's own values - the workflow templates reuse the admission
 * field ids (most notably the "checklist" fieldArray), so a blanket merge of
 * parent values would leak the admission's data into every workflow section.
 */
export const WORKFLOW_SHARED_ANSWER_KEYS = ["isPccEnabled", "pccSettings"];

export const getWorkflowAnswers = (
  parentValues: Record<string, unknown>,
  workflowValues: Record<string, unknown> = {},
): Record<string, unknown> => ({
  ...pick(parentValues, WORKFLOW_SHARED_ANSWER_KEYS),
  ...workflowValues,
});

export const makeWorkflowErrorKeyPrefix = (workflowType: string): string =>
  `workflows.${workflowType}.`;

/**
 * Prefixes flat error/touched keys with "workflows.<type>." so they can be
 * stored on the parent Formik instance next to the admission's own keys.
 */
export const prefixWorkflowKeys = <T>(
  workflowType: string,
  record: Record<string, T>,
): Record<string, T> => {
  const prefix = makeWorkflowErrorKeyPrefix(workflowType);

  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [`${prefix}${key}`, value]),
  );
};

/**
 * Inverse of prefixWorkflowKeys - picks the parent's "workflows.<type>.*" keys
 * and strips the prefix, so they line up with the workflow template's field ids.
 */
export const pickWorkflowKeys = <T>(
  workflowType: string,
  record: Record<string, T> | undefined,
): Record<string, T> => {
  const prefix = makeWorkflowErrorKeyPrefix(workflowType);

  return Object.fromEntries(
    Object.entries(record ?? {})
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.slice(prefix.length), value]),
  );
};

export type WorkflowSettingsValidator = (
  parentValues: Record<string, unknown>,
) => Record<string, string>;

/**
 * Validates values.workflows.<type> against the workflow-settings template's
 * own rules. Returned keys are prefixed, ready to merge into parent errors.
 */
export const validateWorkflowSettings = ({
  workflowType,
  workflowTemplate,
  parentValues,
  isResideAdmin,
}: {
  workflowType: string;
  workflowTemplate: Template | undefined;
  parentValues: Record<string, unknown>;
  isResideAdmin: boolean;
}): Record<string, string> => {
  if (!workflowTemplate) {
    return {};
  }

  const workflows = parentValues.workflows as
    | Record<string, Record<string, unknown>>
    | undefined;
  const answers = getWorkflowAnswers(parentValues, workflows?.[workflowType]);
  const blocks = extractWorkflowSettingsBlocks(
    workflowTemplate,
    answers,
    isResideAdmin,
  );
  const {validate} = getValidationConfig({
    children: blocks,
    answers: answers as any,
  });

  return prefixWorkflowKeys(
    workflowType,
    (validate(answers as any) ?? {}) as Record<string, string>,
  );
};

export const getWorkflowDownloadEmptyPacketTitle = (
  workflowLabel: string,
): string => `Download Empty ${getWorkflowPacketLabel(workflowLabel)}`;

export const extractWorkflowSettingsBlocks = (
  workflowTemplate: Template | undefined,
  mergedAnswers: Record<string, unknown>,
  isResideAdmin: boolean,
): any[] => {
  if (!workflowTemplate) {
    return [];
  }

  const extractedBlocks = extractTemplateSlides(
    workflowTemplate,
    mergedAnswers as any,
  ).flatMap(slide => slide.children);

  return isResideAdmin
    ? withIncludeInAuditLogBlock(extractedBlocks)
    : extractedBlocks;
};

/**
 * Returns a copy of the blocks with INCLUDE_IN_AUDIT_LOG_BLOCK appended to the
 * first "checklist" fieldArray. Never mutates its input - this runs on every
 * validation, and any node shared with the cached template query data would
 * otherwise collect another audit control per run. Idempotent for the same
 * reason.
 */
export const withIncludeInAuditLogBlock = (blocks: any[]): any[] => {
  let isInjected = false;

  const enrich = (node: any): any => {
    if (isInjected || !node || !Array.isArray(node.children)) {
      return node;
    }

    if (node.id === "checklist") {
      isInjected = true;

      return node.children.some(
        (child: any) => child?.id === INCLUDE_IN_AUDIT_LOG_BLOCK.id,
      )
        ? node
        : {...node, children: [...node.children, INCLUDE_IN_AUDIT_LOG_BLOCK]};
    }

    const children = node.children.map(enrich);

    return children.some((child: any, i: number) => child !== node.children[i])
      ? {...node, children}
      : node;
  };

  return blocks.map(enrich);
};

/**
 * Template-independent guard for workflow checklists: every item needs a
 * description. Workflow-template rules only run once that template has loaded
 * and registered its validator, so without this a save while the template is
 * loading (or after it failed to load) could persist an unlabeled item.
 * Returned keys are prefixed like validateWorkflowSettings' errors, which
 * override these when the template is available.
 */
export const validateWorkflowChecklistDescriptions = (
  parentValues: Record<string, unknown>,
): Record<string, string> => {
  const workflows = (parentValues.workflows ?? {}) as Record<
    string,
    Record<string, unknown> | undefined
  >;

  return Object.fromEntries(
    Object.entries(workflows).flatMap(([workflowType, workflowValues]) => {
      const checklist = workflowValues?.checklist;

      if (!Array.isArray(checklist)) {
        return [];
      }

      return checklist.flatMap((item, index) =>
        String(item?.checklistItemDescription ?? "").trim() === ""
          ? [
              [
                `${makeWorkflowErrorKeyPrefix(
                  workflowType,
                )}checklist.${index}.checklistItemDescription`,
                "This field is required.",
              ],
            ]
          : [],
      );
    }),
  );
};

export const getWorkflowNavItems = ({
  workflowType,
  workflowLabel,
  workflowBlocks,
  answers,
  isResideAdmin = false,
}: {
  workflowType: string;
  workflowLabel: string;
  workflowBlocks: ReadonlyArray<any>;
  answers: Record<string, unknown>;
  isResideAdmin?: boolean;
}): WorkflowNavItem[] => {
  const blockItems = filterVisibleFacilitySettingsBlocks(
    workflowBlocks,
    answers,
  ).map(block => ({
    id: makeWorkflowBlockSectionId(workflowType, String(block.id)),
    label: getFacilitySettingsBlockLabel(block),
  }));

  const downloadEmptyPacketItem = {
    id: makeWorkflowDownloadEmptyPacketSectionId(workflowType),
    label: getWorkflowDownloadEmptyPacketTitle(workflowLabel),
  };

  if (!isResideAdmin) {
    return [...blockItems, downloadEmptyPacketItem];
  }

  return [
    ...blockItems,
    {
      id: makeWorkflowDocumentCategorySectionId(workflowType),
      label: "Document Upload Category",
    },
    downloadEmptyPacketItem,
  ];
};
